<template>
  <div v-if="loading">Loading...</div>

  <div v-if="pageData?.contentBlocks">
    <MpfContentRenderer :content-data="pageData?.contentBlocks" :transform-to="withTenantPath" />
  </div>
</template>

<script setup lang="ts">
  import { computed, onUnmounted, ref, watchEffect } from 'vue';
  import { useRoute } from 'vue-router';
  import { useRouteMetaOverrideStore } from '@/stores/useRouteMetaOverrideStore';
  import { router } from '@/router';
  import { useErrorStore } from '@/stores/useErrorStore';
  import MpfContentRenderer from '@/lib/components/MpfContentRenderer/MpfContentRenderer.vue';
  import { withTenantPath } from '@/router/tenantRouting';
  import type { PageContent } from '@/types/pages';

  const metaOverride = useRouteMetaOverrideStore();
  const route = useRoute();
  const errorStore = useErrorStore();
  const pageData = ref<any>(null);
  const loading = ref(false);

  // auto-map all page files
  const pages = import.meta.glob('../../data/pages/**/*.ts');

  const pagePath = computed(() => {
    const pathMatch = route.params.pathMatch;

    if (Array.isArray(pathMatch)) {
      return pathMatch.join('/');
    }

    return String(pathMatch ?? '');
  });

  watchEffect(async (onInvalidate) => {
    let cancelled = false;

    onInvalidate(() => {
      // NB make sure ONLY runs before unMounted with async
      cancelled = true;
    });

    pageData.value = null;

    const metaPageContent = route.meta.pageContent as PageContent['contentBlocks'] | undefined;
    const metaPageTitle = route.meta.pageTitle as string | undefined;
    const metaBackButton = route.meta.backButton as boolean | undefined;

    if (metaPageContent?.length) {
      pageData.value = {
        title: metaPageTitle ?? '',
        contentBlocks: metaPageContent,
      };

      metaOverride.setOverrides({
        pageTitle: metaPageTitle,
        layoutPageTitle: metaPageTitle,
        backButton: metaBackButton,
      });

      return;
    }

    if (!pagePath.value) return;

    try {
      const key = `../../data/pages/${pagePath.value}.ts`;

      const loader = pages[key];

      if (!loader) {
        const message = `Page loader not found for key: ${key}`;
        console.warn(message);
        errorStore.setError({ message: message });
        await router.push({ name: 'error-400-tenant-present' });
        return;
      }

      const mod = await loader();
      if (cancelled) return;

      pageData.value = Object.values(mod)[0];
      metaOverride.setOverrides({
        pageTitle: pageData.value?.title,
        layoutPageTitle: pageData.value?.title,
        backButton: true,
      });
    } catch (err) {
      console.error(err);
      pageData.value = null;
      await router.push({ name: 'error-400-tenant-present' });
    }
  });

  onUnmounted(() => {
    // clear any meta overrides set by this page
    metaOverride.clearOverrides();
  });
</script>
